import { forwardRef, useEffect, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { toast } from 'react-toastify';
import IconCalendar from '@assets/images/icon_calendar.svg';
import { Dropdown } from '@components/common/Dropdown';
import { media } from '@styles/mediaQuery';
import { ko } from 'date-fns/locale';
import dayjs from 'dayjs';
import styled from 'styled-components';

registerLocale('ko', ko);

export enum DateRangePreset {
  CUSTOM = '직접 선택',
  TODAY = '오늘',
  YESTERDAY = '어제',
  THIS_WEEK = '이번 주',
  MONTH = '이번 달',
  LAST_MONTH = '지난달',
  PREV_MONTH = '1개월',
  QUARTER_YEAR = '3개월',
  HALF_YEAR = '6개월',
  YEAR = '1년',
}

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onChange: (event: { target: { name: string; value: string } }) => void;
  defaultPreset?: DateRangePreset;
  maxPeriod?: DateRangePreset;
  disabled?: boolean;
}

export const PRESET_OPTIONS = [
  { value: DateRangePreset.CUSTOM, label: '직접 선택' },
  { value: DateRangePreset.TODAY, label: '오늘' },
  { value: DateRangePreset.YESTERDAY, label: '어제' },
  { value: DateRangePreset.THIS_WEEK, label: '이번 주' },
  { value: DateRangePreset.MONTH, label: '이번 달' },
  { value: DateRangePreset.LAST_MONTH, label: '지난달' },
  { value: DateRangePreset.PREV_MONTH, label: '1개월' },
  { value: DateRangePreset.QUARTER_YEAR, label: '3개월' },
  { value: DateRangePreset.HALF_YEAR, label: '6개월' },
  { value: DateRangePreset.YEAR, label: '1년' },
];

// Custom Input 컴포넌트
const CustomInput = forwardRef<
  HTMLButtonElement,
  { value?: string; onClick?: () => void; disabled?: boolean }
>(({ value, onClick, disabled }, ref) => (
  <RangeButton onClick={onClick} ref={ref} type="button" disabled={disabled}>
    <CalendarIconWrapper>
      <img src={IconCalendar} alt="calendar" />
    </CalendarIconWrapper>
    {value}
  </RangeButton>
));

const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  defaultPreset,
  maxPeriod,
  disabled,
}: DateRangePickerProps) => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    dayjs(startDate).toDate(),
    dayjs(endDate).toDate(),
  ]);
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>(
    defaultPreset || DateRangePreset.CUSTOM,
  );

  const visibleOptions = maxPeriod
    ? PRESET_OPTIONS.slice(0, PRESET_OPTIONS.findIndex((opt) => opt.value === maxPeriod) + 1)
    : PRESET_OPTIONS;

  const getPresetDates = (preset: DateRangePreset): [Date, Date] | null => {
    const today = dayjs();
    let start: Date;
    let end: Date;

    switch (preset) {
      case DateRangePreset.CUSTOM:
        return null;
      case DateRangePreset.TODAY:
        start = today.startOf('day').toDate();
        end = today.endOf('day').toDate();
        break;
      case DateRangePreset.YESTERDAY:
        start = today.subtract(1, 'day').startOf('day').toDate();
        end = today.subtract(1, 'day').endOf('day').toDate();
        break;
      case DateRangePreset.THIS_WEEK:
        start = today.startOf('week').toDate();
        end = today.endOf('week').toDate();
        break;
      case DateRangePreset.MONTH:
        start = today.startOf('month').toDate();
        end = today.endOf('month').toDate();
        break;
      case DateRangePreset.LAST_MONTH:
        start = today.subtract(1, 'month').startOf('month').toDate();
        end = today.startOf('month').subtract(1, 'day').toDate();
        break;
      case DateRangePreset.PREV_MONTH:
        // 1개월 전 다음 날부터 오늘까지
        start = today.subtract(1, 'month').add(1, 'day').startOf('day').toDate();
        end = today.toDate();
        break;
      case DateRangePreset.QUARTER_YEAR:
        // 3개월 전 다음 날부터 오늘까지
        start = today.subtract(3, 'month').add(1, 'day').startOf('day').toDate();
        end = today.toDate();
        break;
      case DateRangePreset.HALF_YEAR:
        // 6개월 전 다음 날부터 오늘까지
        start = today.subtract(6, 'month').add(1, 'day').startOf('day').toDate();
        end = today.toDate();
        break;
      case DateRangePreset.YEAR:
        // 1년 전 다음 날부터 오늘까지
        start = today.subtract(1, 'year').add(1, 'day').startOf('day').toDate();
        end = today.toDate();
        break;
      default:
        start = today.toDate();
        end = today.toDate();
    }

    return [start, end];
  };

  // CustomEvent 기반 필터 초기화 리스너
  useEffect(() => {
    const handleReset = () => {
      if (defaultPreset) {
        const dates = getPresetDates(defaultPreset);
        if (dates) {
          const [start, end] = dates;
          setDateRange([start, end]);
          onChange({ target: { name: 'startDate', value: dayjs(start).format('YYYY-MM-DD') } });
          onChange({ target: { name: 'endDate', value: dayjs(end).format('YYYY-MM-DD') } });
        }
        setSelectedPreset(defaultPreset);
      }
    };

    window.addEventListener('reset-filter', handleReset);
    return () => window.removeEventListener('reset-filter', handleReset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPreset]);

  // maxPeriod에 따른 최대 일수 계산
  const getMaxDays = (): number | null => {
    if (!maxPeriod) return null;

    switch (maxPeriod) {
      case DateRangePreset.TODAY:
      case DateRangePreset.YESTERDAY:
        return 1;
      case DateRangePreset.THIS_WEEK:
        return 7;
      case DateRangePreset.MONTH:
        return 31;
      case DateRangePreset.LAST_MONTH:
        return 31;
      case DateRangePreset.PREV_MONTH:
        return 31;
      case DateRangePreset.QUARTER_YEAR:
        return 93;
      case DateRangePreset.HALF_YEAR:
        return 186;
      case DateRangePreset.YEAR:
        return 365;
      default:
        return null;
    }
  };

  // DatePicker 변경 핸들러
  const handleDateChange = (update: [Date | null, Date | null]) => {
    const [start, end] = update;

    // 범위 검증
    if (start && end && maxPeriod) {
      const maxDays = getMaxDays();
      if (maxDays) {
        const diffDays = dayjs(end).diff(dayjs(start), 'day');
        if (diffDays > maxDays) {
          // 범위 초과 시 시작일 기준으로 최대 범위로 제한
          const adjustedEnd = dayjs(start).add(maxDays, 'day').toDate();
          toast.info(`선택한 기간이 최대 범위(${maxDays}일)를 초과하여 자동으로 조정되었습니다.`, {
            position: 'top-right',
            autoClose: 3000,
          });
          setDateRange([start, adjustedEnd]);
          setSelectedPreset(DateRangePreset.CUSTOM);
          onChange({ target: { name: 'startDate', value: dayjs(start).format('YYYY-MM-DD') } });
          onChange({ target: { name: 'endDate', value: dayjs(adjustedEnd).format('YYYY-MM-DD') } });
          return;
        }
      }
    }

    setDateRange(update);
    setSelectedPreset(DateRangePreset.CUSTOM);

    if (start && end) {
      onChange({ target: { name: 'startDate', value: dayjs(start).format('YYYY-MM-DD') } });
      onChange({ target: { name: 'endDate', value: dayjs(end).format('YYYY-MM-DD') } });
    }
  };

  // 프리셋 선택 핸들러
  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as DateRangePreset;
    if (!value) return;

    setSelectedPreset(value);

    if (value === DateRangePreset.CUSTOM) {
      return;
    }

    const dates = getPresetDates(value);
    if (dates) {
      const [start, end] = dates;
      setDateRange([start, end]);
      onChange({ target: { name: 'startDate', value: dayjs(start).format('YYYY-MM-DD') } });
      onChange({ target: { name: 'endDate', value: dayjs(end).format('YYYY-MM-DD') } });
    }
  };

  return (
    <InputWrapper>
      <DropdownWrapper $disabled={disabled}>
        <Dropdown>
          <select onChange={handlePresetChange} disabled={disabled} value={selectedPreset}>
            {visibleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Dropdown>
      </DropdownWrapper>

      <Container $disabled={disabled}>
        <DatePicker
          selectsRange
          startDate={dateRange[0]}
          endDate={dateRange[1]}
          onChange={handleDateChange}
          monthsShown={2}
          locale="ko"
          dateFormat="yyyy-MM-dd"
          dateFormatCalendar="yyyy년 MM월"
          disabled={disabled}
          customInput={<CustomInput disabled={disabled} />}
          popperPlacement="bottom-end"
          popperClassName="date-range-popper"
        />
      </Container>
    </InputWrapper>
  );
};

export default DateRangePicker;

// Styled Components

const InputWrapper = styled.div`
  width: 100%;
  max-width: 28rem;
  display: flex;
  gap: 0.5rem;
  align-items: stretch;

  > * {
    min-width: 0;
  }
`;

const DropdownWrapper = styled.div<{ $disabled?: boolean }>`
  flex-shrink: 0;
  width: 5rem;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

const Container = styled.div<{ $disabled?: boolean }>`
  flex: 1;
  min-width: 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container {
    width: 100%;
  }
  .react-datepicker__month-container {
    float: left;
  }
  .react-datepicker {
    display: flex;
    flex-direction: row;
  }
  .date-range-popper {
    z-index: 400;
  }
  .react-datepicker-popper {
    z-index: 400;
  }
  ${media.tablet`
    .react-datepicker {
      flex-direction: column;
    }
  `}
`;

const RangeButton = styled.button.attrs({ type: 'button' })`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  gap: 0.25rem;
  border: 1px solid ${({ theme }) => theme.neutralGray[300]};
  border-radius: 0.25rem;

  font-size: 0.875rem;
  min-height: 2.25rem;
  color: ${({ theme }) => theme.neutralGray[600]};
  transition: border-color 0.2s ease;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.neutralGray[100]};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CalendarIconWrapper = styled.span`
  img {
    width: 1rem;
    height: 1rem;
    display: block;
  }
`;
